/**
 * Face-aware caption placement — detects where the speaker's face is in
 * the frame and shifts caption position so subtitles never cover it.
 *
 * Why this matters: Submagic / Captions.ai do this automatically. If the
 * speaker's face is in the bottom half (talking-head selfie), captions
 * sit at top. If face is at top (interview / OBS layout), captions stay
 * bottom. Without face-detection, captions can land squarely over a
 * speaker's mouth — a known UX failure of dumb-position auto-captions.
 *
 * Approach (no extra worker, runs lazily on the main thread):
 *   1. MediaPipe Tasks-Vision FaceDetector loaded from jsDelivr (already
 *      proxied for RU users via /cdn/jsdelivr/). Model file (~3 MB ONNX
 *      derivative) from /cdn/gstorage/ proxy.
 *   2. Sample the loaded <video> at 1-second intervals via OffscreenCanvas
 *      → ImageBitmap. Detector runs per frame, returns 0..N face boxes.
 *   3. Build a sorted list of {t, faces:[{x,y,w,h}]} samples. The pipeline
 *      reads this when computing each line's vertical position.
 *   4. Per caption line, look up the closest face sample. If a face's
 *      bounding box overlaps the line's planned y-range, return a flipped
 *      `\an` alignment so libass renders that line elsewhere.
 *
 * Public API (window.__acFace):
 *   detectAll(video, opts)        — returns Promise<[{t, faces}]>.
 *   placementForLine(faces, linePos, frameSize) — returns {an, hint}.
 *   destroy()                     — free MediaPipe resources.
 *
 * Privacy: video frames never leave the browser. The detector runs
 * locally over canvas-extracted ImageBitmap — no upload, no proxy of
 * pixel data, just inference on-device.
 */
(function(){
    'use strict';

    var TASKS_VISION_VERSION = '0.10.14';
    // Locale-aware base URLs (matches whisper-worker.js / silero-vad-worker.js
    // pattern). RU users get the proxy first, others get direct CDN first.
    function baseUrls(){
        var locale = (typeof window !== 'undefined' && window.__LOCALE)
            ? String(window.__LOCALE).toLowerCase() : 'en';
        var origin = (typeof window !== 'undefined' && window.location)
            ? window.location.origin : '';
        var jsdelivrProxy = origin + '/cdn/jsdelivr/npm/@mediapipe/tasks-vision@' + TASKS_VISION_VERSION;
        var jsdelivrDirect = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + TASKS_VISION_VERSION;
        var modelProxy  = origin + '/cdn/gstorage/mediapipe-models/face_detector/blaze_face_short_range/float16/latest/blaze_face_short_range.tflite';
        var modelDirect = 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/latest/blaze_face_short_range.tflite';
        return locale === 'ru'
            ? { libs: [jsdelivrProxy, jsdelivrDirect], model: [modelProxy, modelDirect] }
            : { libs: [jsdelivrDirect, jsdelivrProxy], model: [modelDirect, modelProxy] };
    }

    var modCache = null, detectorCache = null;

    async function loadDetector() {
        if (detectorCache) return detectorCache;
        var urls = baseUrls();
        var mod = null;
        var lastErr = null;
        // Track which lib URL actually succeeded — the WASM fileset that
        // backs FilesetResolver MUST come from the same origin, or we get
        // CORS rejections and silent feature-detection failures. The prior
        // code used urls.libs[0] unconditionally, which sent the fileset
        // to the FIRST candidate even when the actual import had fallen
        // through to candidate 2 or 3 (defeating the dual-CDN fallback).
        var libBase = null;
        for (var i = 0; i < urls.libs.length; i++) {
            try {
                mod = await import(urls.libs[i] + '/vision_bundle.mjs');
                libBase = urls.libs[i];
                break;
            } catch(e) { lastErr = e; }
        }
        if (!mod) throw new Error('MediaPipe load failed: ' + (lastErr && lastErr.message));
        modCache = mod;

        // FilesetResolver pulls the WASM runtime from the SAME origin as the
        // imported lib — required for SAB / shared-state plumbing inside the
        // MediaPipe runtime.
        var fileset = await mod.FilesetResolver.forVisionTasks(libBase + '/wasm');

        // Try each model URL with a deadline — RU proxy might 404 some files.
        for (var j = 0; j < urls.model.length; j++) {
            try {
                var fdOpts = {
                    baseOptions: { modelAssetPath: urls.model[j] },
                    runningMode: 'IMAGE',
                    minDetectionConfidence: 0.5,
                };
                // Без WebGL2 — CPU и программный «WebGL2» (timbrica-core.js, __mpSoftFit).
                if (window.__mpSoftFit) await window.__mpSoftFit(fdOpts);
                detectorCache = await mod.FaceDetector.createFromOptions(fileset, fdOpts);
                return detectorCache;
            } catch(e) { lastErr = e; }
        }
        throw new Error('FaceDetector model load failed: ' + (lastErr && lastErr.message));
    }

    /* Sample the video element at `interval` seconds via canvas → ImageBitmap.
       Skip frames that read black (object-fit:contain or letterbox at top/bottom).
       Returns Promise<[{t, faces}]>. */
    async function detectAll(video, opts) {
        opts = opts || {};
        var interval = opts.interval != null ? opts.interval : 1.0;
        var maxSamples = opts.maxSamples != null ? opts.maxSamples : 60;
        if (!video || !video.duration || video.duration < 0.5) return [];
        var detector = await loadDetector();

        var canvas = document.createElement('canvas');
        var w = video.videoWidth || 640;
        var h = video.videoHeight || 360;
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');

        var samples = [];
        var step = Math.max(0.5, Math.min(interval, video.duration / Math.max(1, maxSamples)));
        var savedPaused = video.paused;
        var savedTime = video.currentTime;
        try {
            for (var t = 0; t < video.duration; t += step) {
                if (samples.length >= maxSamples) break;
                if (window.__seekPresented) {
                    // Presented-frame wait: drawImage on bare seeked races
                    // the compositor -> face detection on a black frame.
                    await window.__seekPresented(video, t, 1500);
                } else {
                    video.currentTime = t;
                    await waitForSeeked(video, 1500);
                }
                ctx.drawImage(video, 0, 0, w, h);
                // FaceDetector accepts ImageData / ImageBitmap / VideoFrame.
                var imgData = ctx.getImageData(0, 0, w, h);
                try {
                    var result = detector.detect(imgData);
                    var faces = (result && result.detections || []).map(function(d){
                        var bb = d.boundingBox || {};
                        return {
                            x: (bb.originX || 0) / w,
                            y: (bb.originY || 0) / h,
                            w: (bb.width || 0) / w,
                            h: (bb.height || 0) / h,
                        };
                    });
                    samples.push({ t: t, faces: faces });
                } catch(detErr) {
                    samples.push({ t: t, faces: [] });
                }
            }
        } finally {
            // Restore video position
            try { video.currentTime = savedTime; } catch(_) {}
            if (!savedPaused && video.paused) try { video.play(); } catch(_) {}
        }
        return samples;
    }

    function waitForSeeked(video, deadlineMs) {
        return new Promise(function(resolve){
            var done = false;
            var timer = setTimeout(function(){
                if (done) return;
                done = true; resolve();
            }, deadlineMs);
            var onSeeked = function(){
                if (done) return;
                done = true;
                clearTimeout(timer);
                video.removeEventListener('seeked', onSeeked);
                resolve();
            };
            video.addEventListener('seeked', onSeeked);
        });
    }

    /* Compute the placement override for a single caption line given the
       nearest face sample. Returns one of:
         { an: 8, hint: 'top' }     — face is bottom-half, push captions up
         { an: 2, hint: 'bottom' }  — face is top-half, push captions down
         null                       — no face, leave as-is
    */
    function placementForLine(faces, lineStartSec, samples) {
        if (!samples || !samples.length) return null;
        // Find the closest sample by time
        var best = samples[0];
        var bestDist = Math.abs(samples[0].t - lineStartSec);
        for (var i = 1; i < samples.length; i++) {
            var d = Math.abs(samples[i].t - lineStartSec);
            if (d < bestDist) { best = samples[i]; bestDist = d; }
        }
        if (!best.faces.length) return null;
        // Use the largest face (likely the speaker)
        var face = best.faces.reduce(function(a, b){ return (a.w * a.h) > (b.w * b.h) ? a : b; });
        // Centre Y of the face in [0..1]
        var cy = face.y + face.h * 0.5;
        if (cy > 0.55) {
            return { an: 8, hint: 'top' }; // face in lower half → caption on top
        } else if (cy < 0.45) {
            return { an: 2, hint: 'bottom' }; // face in upper half → caption on bottom
        }
        return null; // face is mid-frame, no clear winner
    }

    function destroy() {
        try { if (detectorCache) detectorCache.close(); } catch(_) {}
        detectorCache = null;
        modCache = null;
    }

    window.__acFace = {
        detectAll: detectAll,
        placementForLine: placementForLine,
        destroy: destroy,
    };
})();
